/**
 * Service create/edit form component
 */
import { useState, useEffect, useRef } from 'react';
import { Service, Branch } from '@/types';
import { CreateServiceInput, UpdateServiceInput } from '@/contexts/ServiceContext';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { toast } from 'sonner';

interface ServiceFormProps {
  service?: Service;
  franchiseId: string;
  branches: Branch[];
  onSubmit: (data: CreateServiceInput | UpdateServiceInput) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

const SERVICE_CATEGORIES = [
  'Haircut',
  'Beard',
  'Shave',
  'Styling',
  'Color',
  'Treatment',
  'Kids',
  'Special',
  'Package',
  'Other',
];

export function ServiceForm({ service, franchiseId, branches, onSubmit, onCancel, isLoading }: ServiceFormProps) {
  const [formData, setFormData] = useState<CreateServiceInput & { imageUrl?: string }>({
    franchiseId,
    branchId: '',
    name: '',
    description: '',
    duration: 30,
    price: 0,
    category: 'Haircut',
    isActive: true,
    imageUrl: '',
  });

  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (service) {
      setFormData({
        franchiseId: service.franchiseId,
        branchId: service.branchId || '',
        name: service.name,
        description: service.description || '',
        duration: service.duration,
        price: service.price,
        category: service.category,
        isActive: service.isActive,
        imageUrl: service.imageUrl || '',
      });
      setImagePreview(service.imageUrl || null);
    }
  }, [service]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
  };

  const formatPrice = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  const handlePriceChange = (value: string) => {
    const euros = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, price: Math.round(euros * 100) }));
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    const toastId = toast.loading('Uploading image...');

    try {
      // Create unique filename
      const timestamp = Date.now();
      const filename = `services/${franchiseId}/${timestamp}_${file.name}`;
      const storageRef = ref(storage, filename);
      const uploadTask = uploadBytesResumable(storageRef, file);

      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          toast.loading(`Uploading... ${progress.toFixed(0)}%`, { id: toastId });
        },
        (error) => {
          console.error('Error uploading image:', error);
          toast.error('Error uploading image', { id: toastId });
          setUploadingImage(false);
        },
        async () => {
          // Get download URL
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          // Update form data with imageUrl
          setFormData(prev => ({ ...prev, imageUrl: downloadURL }));
          setImagePreview(downloadURL);

          toast.success('Image uploaded successfully!', { id: toastId });
          setUploadingImage(false);
        }
      );
    } catch (error) {
      console.error('Error in image upload:', error);
      toast.error('Error uploading image', { id: toastId });
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setFormData(prev => ({ ...prev, imageUrl: '' }));
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    toast.success('Image removed');
  };

  return (
    <Card className="p-6 max-w-2xl mx-auto">
      <h2 className="text-2xl font-bold mb-6">
        {service ? 'Edit Service' : 'Create New Service'}
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-ink-muted mb-2">
            Service Name *
          </label>
          <input
            id="name"
            type="text"
            required
            maxLength={100}
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
            placeholder="Classic Haircut"
          />
          <p className="text-xs text-ink-subtle mt-1">{formData.name.length}/100 characters</p>
        </div>

        {/* Branch */}
        <div>
          <label htmlFor="branchId" className="block text-sm font-medium text-ink-muted mb-2">
            Sucursal *
          </label>
          <select
            id="branchId"
            required
            value={formData.branchId}
            onChange={(e) => setFormData(prev => ({ ...prev, branchId: e.target.value }))}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
          >
            <option value="">Selecciona una sucursal</option>
            {branches.map(branch => (
              <option key={branch.branchId} value={branch.branchId}>
                {branch.name}
              </option>
            ))}
          </select>
          <p className="text-xs text-ink-subtle mt-1">
            Este servicio estará disponible solo en la sucursal seleccionada
          </p>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-ink-muted mb-2">
            Description
          </label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
            maxLength={500}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
            placeholder="Brief description of the service"
          />
          <p className="text-xs text-ink-subtle mt-1">{(formData.description || '').length}/500 characters</p>
        </div>

        {/* Service Image */}
        <div>
          <label className="block text-sm font-medium text-ink-muted mb-2">
            Service Image
          </label>

          {imagePreview ? (
            <div className="space-y-3">
              <div className="relative w-full h-48 rounded-lg overflow-hidden border-2 border-hairline">
                <img
                  src={imagePreview}
                  alt="Service preview"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  className="flex-1 px-4 py-2 bg-info text-white rounded-lg hover:bg-info/90 transition disabled:opacity-50"
                >
                  {uploadingImage ? 'Uploading...' : 'Change Image'}
                </button>
                <button
                  type="button"
                  onClick={handleRemoveImage}
                  disabled={uploadingImage}
                  className="px-4 py-2 bg-danger text-white rounded-lg hover:bg-danger/90 transition disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            </div>
          ) : (
            <div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingImage}
                className="w-full px-4 py-3 border-2 border-dashed border-hairline rounded-lg hover:border-info/30 dark:hover:border-info/30 transition flex items-center justify-center gap-2 text-ink-muted hover:text-info disabled:opacity-50 bg-surface"
              >
                <span className="text-2xl">📷</span>
                <span>{uploadingImage ? 'Uploading...' : 'Upload Service Image'}</span>
              </button>
              <p className="text-xs text-ink-subtle mt-2">
                Recommended: 800x600px or larger. Max 5MB. JPG, PNG, or WebP.
              </p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
            disabled={uploadingImage}
          />
        </div>

        {/* Category */}
        <div>
          <label htmlFor="category" className="block text-sm font-medium text-ink-muted mb-2">
            Category *
          </label>
          <select
            id="category"
            required
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
          >
            {SERVICE_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>

        {/* Duration and Price */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="duration" className="block text-sm font-medium text-ink-muted mb-2">
              Duration (minutes) *
            </label>
            <input
              id="duration"
              type="number"
              required
              min={5}
              max={480}
              step={5}
              value={formData.duration}
              onChange={(e) => setFormData(prev => ({ ...prev, duration: parseInt(e.target.value) || 30 }))}
              className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
            />
            <p className="text-xs text-ink-subtle mt-1">
              Duration: {formatDuration(formData.duration)}
            </p>
          </div>

          <div>
            <label htmlFor="price" className="block text-sm font-medium text-ink-muted mb-2">
              Price (€) *
            </label>
            <input
              id="price"
              type="number"
              required
              min={0}
              step={0.01}
              value={formatPrice(formData.price)}
              onChange={(e) => handlePriceChange(e.target.value)}
              className="w-full px-4 py-2 border border-hairline rounded-lg focus:ring-2 focus:ring-focus focus:border-transparent bg-surface text-ink"
            />
            <p className="text-xs text-ink-subtle mt-1">
              Price: €{formatPrice(formData.price)}
            </p>
          </div>
        </div>

        {/* Quick Duration Presets */}
        <div>
          <label className="block text-sm font-medium text-ink-muted mb-2">Quick Duration Presets</label>
          <div className="flex flex-wrap gap-2">
            {[15, 30, 45, 60, 90, 120].map((minutes) => (
              <button
                key={minutes}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, duration: minutes }))}
                className={`px-3 py-1 rounded-full text-sm ${
                  formData.duration === minutes
                    ? 'bg-info text-white'
                    : 'bg-surface-3 text-ink-muted hover:bg-surface-3'
                }`}
              >
                {formatDuration(minutes)}
              </button>
            ))}
          </div>
        </div>

        {/* Is Active */}
        <div className="flex items-center">
          <input
            id="isActive"
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
            className="w-4 h-4 text-info border-hairline rounded focus:ring-focus bg-surface"
          />
          <label htmlFor="isActive" className="ml-2 text-sm font-medium text-ink-muted">
            Active (visible to customers)
          </label>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 pt-4">
          <Button type="submit" isLoading={isLoading} className="flex-1">
            {service ? 'Update Service' : 'Create Service'}
          </Button>
          <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading} className="flex-1">
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
